import React, { Component } from 'react'
import { Alert, Button, Col, FormGroup, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap'
import { graphql } from '@apollo/client/react/hoc'
import gql from 'graphql-tag'
import moment from 'moment'
import Select from 'react-select'

import toTitleCase from '../../Functions/toTitleCase'
import withApolloClient from '../../withApolloClient'
import './AddInspectionModal.css'

const AllCarConditionsQuery = gql`query AddInspectionAllCarConditions($carId: ID!, $rentalId: ID) {
    allCarConditions(carId: $carId, rentalId: $rentalId, orderBy: ["-id"]) {
        edges {
            node {
                id
                pk
                dateAdded
                formType
                rental {
                    id
                    startDate
                    endDate
                    driver {
                        id
                        name
                    }
                }
            }
        }
    }
}`

const UpdateIncidentMutation = gql`mutation UpdateIncident($input: UpdateIncidentMutationInput!) {
    updateIncident(input: $input) {
        ok
        errors {
            field
            messages
        }
    }
}`

class AddInspectionModal extends Component {
    constructor(props) {
        super(props)
        this.state = {
            selectedCarCondition: null,
            error: '',
            saving: false,
        }
    }

    handleClose = () => {
        this.setState({ selectedCarCondition: null, error: '', saving: false })
        this.props.handleClose && this.props.handleClose()
    }

    handleSubmit = () => {
        const { selectedCarCondition } = this.state

        if (!selectedCarCondition) {
            this.setState({ error: 'Please select an inspection.' })
            return
        }

        this.setState({ saving: true, error: '' })
        this.props.apolloClient.mutate({
            mutation: UpdateIncidentMutation,
            variables: {
                input: {
                    incidentId: this.props.incidentId,
                    carConditionId: selectedCarCondition.value,
                },
            },
        }).then((result) => {
            const response = result?.data?.updateIncident
            if (response?.ok) {
                const refetchPromise = this.props.refetchQuery ? this.props.refetchQuery() : Promise.resolve()
                return Promise.resolve(refetchPromise).catch(() => null).finally(this.handleClose)
            } else {
                const error = response?.errors?.[0]?.messages?.toString() || 'Could not add inspection to incident.'
                this.setState({ error, saving: false })
            }
        }).catch(() => {
            this.setState({ error: 'An error has occurred.', saving: false })
        })
    }

    buildInspectionOption = (edge) => {
        const node = edge.node
        const rental = node.rental
        const date = node.dateAdded ? moment(node.dateAdded).tz('America/New_York').format('MMM DD, YYYY h:mm A') : '--'
        const formType = node.formType ? toTitleCase(node.formType) : 'Inspection'
        const formTypeKey = formType.toLowerCase().replace(/\s+/g, '-')
        const driverName = rental?.driver?.name || 'Driver not linked'
        const rentalStart = rental?.startDate ? moment(rental.startDate).tz('America/New_York').format('MMM DD, YYYY') : '--'
        const rentalEnd = rental?.endDate ? moment(rental.endDate).tz('America/New_York').format('MMM DD, YYYY') : '--'
        const rentalRange = rental ? `${rentalStart} - ${rentalEnd}` : 'Rental not linked'

        return {
            value: node.id,
            label: `#${node.pk} - ${formType} - ${date} - ${driverName} - ${rentalRange}`,
            pk: node.pk,
            formType,
            formTypeKey,
            date,
            driverName,
            rentalRange,
        }
    }

    formatInspectionOptionLabel = (option, { context }) => {
        if (context === 'value') {
            return (
                <div className="add-inspection-select-value">
                    <span className="add-inspection-select-value__title">#{option.pk} - {option.formType}</span>
                    <span className="add-inspection-select-value__meta">{option.driverName} | {option.rentalRange}</span>
                </div>
            )
        }
        return (
            <div className="add-inspection-select-option">
                <div className="add-inspection-select-option__primary">
                    <div className="add-inspection-select-option__identity">
                        <span className="add-inspection-select-option__id">#{option.pk}</span>
                        <span className={`add-inspection-select-option__type add-inspection-select-option__type--${option.formTypeKey}`}>
                            {option.formType}
                        </span>
                    </div>
                    <span className="add-inspection-select-option__date">
                        <i className="fa fa-clock-o" aria-hidden="true" />
                        {option.date}
                    </span>
                </div>
                <div className="add-inspection-select-option__meta">
                    <span className="add-inspection-select-option__meta-group">
                        <i className="fa fa-user-o" aria-hidden="true" />
                        <span className="add-inspection-select-option__meta-label">Driver</span>
                        <span className="add-inspection-select-option__meta-value">{option.driverName}</span>
                    </span>
                    <span className="add-inspection-select-option__divider" aria-hidden="true" />
                    <span className="add-inspection-select-option__meta-group">
                        <i className="fa fa-calendar-o" aria-hidden="true" />
                        <span className="add-inspection-select-option__meta-label">Agreement</span>
                        <span className="add-inspection-select-option__meta-value">{option.rentalRange}</span>
                    </span>
                </div>
            </div>
        )
    }

    render() {
        const { isOpen, conditionsLoading, allCarConditions } = this.props
        const { selectedCarCondition, error, saving } = this.state
        const inspectionOptions = allCarConditions?.edges?.map(this.buildInspectionOption) || []

        return (
            <Modal isOpen={isOpen} toggle={this.handleClose} className="incident-modal add-inspection-modal">
                <ModalHeader>Add Inspection</ModalHeader>
                <ModalBody>
                    {error && <Alert color="danger">{error}</Alert>}
                    <Row>
                        <Col xs={12}>
                            <FormGroup>
                                <Label>Inspection</Label>
                                <Select
                                    className="add-inspection-select"
                                    classNamePrefix="add-inspection-select"
                                    options={inspectionOptions}
                                    value={selectedCarCondition}
                                    onChange={(selectedCarCondition) => this.setState({ selectedCarCondition, error: '' })}
                                    formatOptionLabel={this.formatInspectionOptionLabel}
                                    placeholder="Select inspection"
                                    isLoading={conditionsLoading}
                                    isDisabled={conditionsLoading || saving}
                                    noOptionsMessage={() => conditionsLoading ? 'Loading inspections...' : 'No inspections found'}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={this.handleClose} disabled={saving}>Cancel</Button>
                    <Button color="primary" onClick={this.handleSubmit} disabled={saving || !selectedCarCondition}>
                        {saving ? 'Adding...' : 'Add Inspection'}
                    </Button>
                </ModalFooter>
            </Modal>
        )
    }
}

export default graphql(AllCarConditionsQuery, {
    options: (props) => ({
        variables: {
            carId: props.carId,
            // rentalId: props.rentalId || null,
            rentalId: null,
        },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    }),
    props: ({ data: { loading, allCarConditions } }) => ({
        conditionsLoading: loading,
        allCarConditions,
    }),
})(withApolloClient(AddInspectionModal))
